'use client'

import { queryOptions, useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useEffect, useMemo } from 'react'

import {
  getBlockBySlug,
  getCompilationBySlug,
  getProgramBySlug,
} from '@src/actions/content.actions'
import { usePostWorkoutExperienceController } from '@src/hooks/usePostWorkoutExperience'
import { useLocale } from '@src/i18n/LocaleProvider'
import {
  type CommitMode,
  type TrackableContent,
  getCommitMode,
  getEnrolledTrack,
  getTrackBadgeEntity,
  getTracks,
  hasLegacyProgress,
  isContentEnrolled,
} from '@src/mappers/collection/track.mapper'
import type { ParentContent } from '@src/utils/query.util'
import { type ParentContentTypes, getContentDetailsQueryKey } from '@src/utils/query.util'

type ContentFn = (args: { slug: string; locale: string }) => Promise<ParentContent | null>

export type CdpContent = ParentContent | null | undefined

const getContentAction = (contentType: ParentContentTypes): ContentFn | undefined => {
  const contentFetchAction = {
    [CocoApi.ContentType.program]: getProgramBySlug as ContentFn,
    [CocoApi.ContentType.compilation]: getCompilationBySlug as ContentFn,
    [CocoApi.ContentType.bodiBlock]: getBlockBySlug as ContentFn,
  }
  return contentFetchAction[contentType] ?? undefined
}

export const getContentDetailsQueryOptions = (
  contentType: ParentContentTypes,
  slug: string | undefined,
  locale: string
) =>
  queryOptions({
    queryKey: getContentDetailsQueryKey(contentType, slug, locale),
    queryFn: (): Promise<CdpContent> =>
      getContentAction(contentType)?.({ slug: slug as string, locale }) ?? Promise.resolve(null),
    enabled: Boolean(slug),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

/** Query key inputs only; stable per route, so readers never re-render from context. */
interface ContentQueryKeyContextType {
  contentType: ParentContentTypes
  slug?: string
  locale: string
  sectionSlug?: string
}

const ContentDataContext = createContext<ContentQueryKeyContextType | undefined>(undefined)

const NO_CONTENT_KEY: ContentQueryKeyContextType = {
  contentType: 'program' as ParentContentTypes,
  locale: '',
}

/** Pass a module-level `select`; an inline function re-runs on every render. */
export function useCdpContent(): CdpContent
export function useCdpContent<T>(select: (content: CdpContent) => T): T | undefined
export function useCdpContent<T>(select?: (content: CdpContent) => T) {
  const { contentType, slug, locale } = useContext(ContentDataContext) ?? NO_CONTENT_KEY
  const { data } = useQuery({ ...getContentDetailsQueryOptions(contentType, slug, locale), select })
  return data
}

const NO_TRACKS: CocoApi.TrackEntity[] = []

const getEnrolledTrackBadgeEntity = (content: TrackableContent) =>
  getTrackBadgeEntity(getEnrolledTrack(content), content?.entities)

/** Whether the user is enrolled in this content or in one of its tracks. */
export const useIsContentEnrolled = (): boolean => useCdpContent(isContentEnrolled) ?? false
export const useCommitMode = (): CommitMode => useCdpContent(getCommitMode) ?? 'program'
export const useEnrolledTrack = () => useCdpContent(getEnrolledTrack) ?? null
export const useEnrolledTrackBadgeEntity = () => useCdpContent(getEnrolledTrackBadgeEntity) ?? null
export const useTracks = () => useCdpContent(getTracks) ?? NO_TRACKS
export const useHasLegacyProgress = (): boolean => useCdpContent(hasLegacyProgress) ?? false

export const useContentDetailsData = (): {
  content?: CdpContent
  sectionSlug?: string
  enrolledTrack?: CocoApi.TrackEntity | null
  contentType?: ParentContentTypes
} => {
  const keyInputs = useContext(ContentDataContext)
  const content = useCdpContent()
  const enrolledTrack = useEnrolledTrack()

  if (!keyInputs) return {}

  return {
    content,
    sectionSlug: keyInputs.sectionSlug ?? content?.sections?.[0]?.slug,
    enrolledTrack,
    contentType: keyInputs.contentType,
  }
}

interface ContentDataProviderProps {
  children: React.ReactNode
  sectionSlug?: string
  slug?: string
  contentType?: CocoApi.ContentType
}

export function ContentDetailsDataProvider({
  contentType = 'program' as Exclude<CocoApi.ContentType, CocoApi.ContentType.customCollection>,
  children,
  slug,
  sectionSlug,
}: ContentDataProviderProps) {
  const {
    locale: { code: locale },
  } = useLocale()

  const keyInputs = useMemo(
    () => ({ contentType: contentType as ParentContentTypes, slug, locale, sectionSlug }),
    [contentType, slug, locale, sectionSlug]
  )

  return (
    <ContentDataContext.Provider value={keyInputs}>
      <PostWorkoutContentSync />
      {children}
    </ContentDataContext.Provider>
  )
}

/*
 * Pass CDP content to the PWE context which sits at root with VideoPlayerProvider
 * to maintain favorite button state between CDP <--> PWE
 */
function PostWorkoutContentSync() {
  const content = useCdpContent()
  const { setPostWorkoutParentContent } = usePostWorkoutExperienceController()

  useEffect(() => {
    setPostWorkoutParentContent(content)
    return () => setPostWorkoutParentContent(undefined)
  }, [content, setPostWorkoutParentContent])

  return null
}
